import React from 'react';
import { CheckCircleOutlined, LoadingOutlined, WarningOutlined } from '@ant-design/icons';
import { Spin } from 'antd';

/**
 * ConnectionStatus Component
 * Displays the current connection state with visual indicators
 * 
 * @param {string} connectionState - 'connected', 'reconnecting', or 'disconnected'
 * @param {boolean} compact - If true, shows compact version for inline display
 * @param {number} reconnectAttempt - Current reconnection attempt number (optional)
 * @param {number} maxAttempts - Maximum reconnection attempts (optional)
 * @param {boolean} showWhenConnected - If true, shows status even when connected (for recording active state)
 */
const ConnectionStatus = ({ 
  connectionState = 'disconnected', 
  compact = false,
  reconnectAttempt = 0,
  maxAttempts = 5,
  isRecording = false,
  isOnline = true,
  wsState = 'idle',
  showWhenConnected = false,
  wsConnectionFailed = false,
}) => {
  // Show when explicitly requested (e.g., during recording) OR when not connected
  // Also always show when WebSocket connection has permanently failed during recording
  if (connectionState === 'connected' && !compact && !showWhenConnected && !wsConnectionFailed) {
    return null;
  }

  const getStatusConfig = () => {
    switch (connectionState) {
      case 'connected':
        // Only show if recording is active and showWhenConnected is true
        if (isRecording && showWhenConnected) {
          return {
            icon: (
              <div style={{ 
                width: '12px', 
                height: '12px', 
                borderRadius: '50%', 
                backgroundColor: '#52c41a',
                animation: 'pulse 2s infinite',
              }} />
            ),
            color: '#52c41a',
            bgColor: '#f6ffed',
            borderColor: '#b7eb8f',
            text: 'Recording Active',
            description: 'Your session is being recorded and transcribed in real-time.',
          };
        }
        return {
          icon: <CheckCircleOutlined style={{ fontSize: compact ? '14px' : '16px' }} />,
          color: '#52c41a',
          bgColor: '#f6ffed',
          borderColor: '#b7eb8f',
          text: 'Connected',
          description: 'Recording in progress',
        };
      case 'reconnecting':
        return {
          icon: <Spin indicator={<LoadingOutlined style={{ fontSize: compact ? '14px' : '16px' }} spin />} />,
          color: '#faad14',
          bgColor: '#fffbe6',
          borderColor: '#ffe58f',
          text: isOnline ? 'Reconnecting to Server' : 'Reconnecting',
          description: isOnline
            ? (reconnectAttempt > 0 
                ? `Reconnecting to server... (${reconnectAttempt}/${maxAttempts}). Recording is paused.`
                : 'Reconnecting to server... Recording is paused.')
            : (reconnectAttempt > 0 
                ? `Reconnecting... (${reconnectAttempt}/${maxAttempts}). Recording is paused.`
                : 'Reconnecting... Recording is paused.'),
        };
      case 'disconnected':
        // Check if it's actually a network issue or just not recording
        if (!isOnline) {
          // Network is offline
          return {
            icon: <WarningOutlined style={{ fontSize: compact ? '14px' : '16px' }} />,
            color: '#ff4d4f',
            bgColor: '#fff2f0',
            borderColor: '#ffccc7',
            text: 'No Internet Connection',
            description: isRecording 
              ? 'Recording paused. Will resume when connection is restored.'
              : 'Internet connection is required to start recording.',
          };
        }

        // WebSocket connection failed during active recording — reconnection attempts exhausted
        if (wsConnectionFailed && isRecording) {
          return {
            icon: <WarningOutlined style={{ fontSize: compact ? '14px' : '16px' }} />,
            color: '#ff4d4f',
            bgColor: '#fff2f0',
            borderColor: '#ffccc7',
            text: 'Recording Connection Lost',
            description: 'The connection to the recording server was lost. Please stop the recording and start again.',
          };
        }

        return null;
      default:
        return null;
    }
  };

  const config = getStatusConfig();
  if (!config) return null;

  // Compact version for inline display (near recording controls)
  if (compact) {
    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 10px',
          borderRadius: '12px',
          backgroundColor: config.bgColor,
          border: `1px solid ${config.borderColor}`,
          fontSize: '12px',
          fontWeight: '500',
          color: config.color,
        }}
      >
        {config.icon}
        <span>{config.text}</span>
      </div>
    );
  }

  // Full banner version for top of page
  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.1);
          }
        }
      `}</style>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          padding: '12px 24px',
          backgroundColor: config.bgColor,
          border: `1px solid ${config.borderColor}`,
          borderRadius: '8px',
          marginBottom: '16px',
        }}
      >
      <div style={{ color: config.color, display: 'flex', alignItems: 'center' }}>
        {config.icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: '600', color: config.color, fontSize: '14px' }}>
          {config.text}
        </div>
        <div style={{ fontSize: '13px', color: '#666', marginTop: '2px' }}>
          {config.description}
        </div>
      </div>
      </div>
    </>
  );
};

export default ConnectionStatus;
